import { CHANNEL_PRODUCT_TYPES, CHANNEL_OPTIONS_EXTERIOR } from '@/utils/campaignOptions'
import channelService from '@/api/channelService'
import i18n from '@/i18n'

export default {
  name: 'ExteriorChannelSidebar',
  mixins: [],
  components: {
    TagListSelector: () => import(/* webpackChunkName: "TagListSelector" */ '@/components/tag-list-selector/TagListSelector.vue'),
    ProductSelector: () => import(/* webpackChunkName: "ProductSelector" */ '@/components/product-selector/ProductSelector.vue')
  },
  props: {
    campaign: {
      type: Object,
      required: true
    },
    channel: {
      type: Object,
      required: true
    },
    boxSelectionEnabled: {
      type: Number,
      required: false
    },
    kpisDirty: {
      type: Boolean,
      default: false
    },
    adverts: {
      type: Object,
      required: true
    },
    advertCampaigns: {
      type: Object,
      required: true
    },
    kpis: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      kpisOpen: true,
      productTypeOptions: Object.values(CHANNEL_PRODUCT_TYPES).slice(0, 2)
    }
  },
  computed: {
    productSelectedInfo () {
      const numProducts = this.channel.products.length
      return this.isService
        ? i18n.tc('N_SERVICES_SELECTED', numProducts)
        : i18n.tc('N_PRODUCTS_SELECTED', numProducts)
    },
    productTypeLabelPluralized () {
      return this.isService
        ? this.hasMultipleProducts
          ? i18n.t('SERVICES')
          : i18n.t('SERVICE')
        : this.hasMultipleProducts
          ? i18n.t('PRODUCTS')
          : i18n.t('PRODUCT')
    },
    productTypeLabel () {
      return this.isService
        ? CHANNEL_PRODUCT_TYPES.DIRECT.label
        : CHANNEL_PRODUCT_TYPES.REDIRECT.label
    },
    isProduct () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.REDIRECT.value
    },
    isService () {
      return this.channel.direction === CHANNEL_PRODUCT_TYPES.DIRECT.value
    },
    campaignType () {
      return CHANNEL_PRODUCT_TYPES[this.channel.direction].label
    },
    campaignShopIds () {
      return this.campaign.shops.map(s => s.id)
    },
    type () {
      return this.$route.params.channeltype
    },
    isNew () {
      return this.$route.params.channelid === 'new'
    },
    isSignaling () {
      return this.$route.params.channeltype === CHANNEL_OPTIONS_EXTERIOR.SIGNALING
    },
    hasMultipleProducts () {
      return !this.isService
    },
    advertsSelectedForProducts () {
      return Object.fromEntries(this.channel.products.map(p => [
        p.id,
        this.getAdvertsForProduct(p.id)
      ]))
    },
    advertOptionsForProducts () {
      const selectedAdvertIds = this.channel.productsAdverts.map(pa => pa.advert)
      return Object.fromEntries(this.channel.products.map(p => [
        p.id,
        [
          ...this.getAdvertsForProduct(p.id),
          ...this.adverts.data.filter(adv => {
            const notSelected = selectedAdvertIds.indexOf(adv.id) === -1
            const notReserved = this.isSignaling ? true : this.advertCampaigns[adv.id].length === 0
            const sameShop = this.isSignaling ? true : adv.shop_related.id === p.shop_related.id
            return notSelected && notReserved && sameShop
          })
        ]
      ]))
    }
  },
  beforeDestroy () {},
  mounted () {
    if (this.isNew && this.isSignaling) {
      channelService.getSummary('products', 'FAKE', {
        reference: 'FAKE',
        shop_related__in: this.shops
      }, true).then(data => {
        this.channel.products = [data[0]]
      }).catch(err => {
        // eslint-disable-next-line
        console.error('[product-selector.js] Error searching FAKE products', err)
      })
    }
  },
  methods: {
    updateScrollbar () {
      window.setTimeout(() => {
        if (this.$refs.scrollbar) this.$refs.scrollbar.update()
      }, 250)
    },
    updateKPIs () {
      this.$emit('updateKPIs')
    },
    onDirectionChange () {
      for (const product of this.channel.products) {
        this.removeProduct(product)
      }
    },
    removeProduct (product) {
      this.channel.products = this.channel.products.filter(p => p.id !== product.id)
      this.channel.productsAdverts = this.channel.productsAdverts.filter(pa => pa.product !== product.id)
    },
    editProduct (product) {
      this.channel.products = this.channel.products.map(p => {
        if (p.id === product.id) {
          p.is_edited = true
        }
        return p
      })
    },
    revertEditProduct (product) {
      this.channel.products = this.channel.products.map(p => {
        if (p.id === product.id) {
          p.is_edited = false
        }
        return p
      })
    },
    getAdvertsForProduct (productId) {
      return this.adverts.data.filter(adv => (
        this.channel.productsAdverts.some(pa => pa.product === productId && pa.advert === adv.id)
      ))
    },
    updateAdvertsForProduct (productId, adverts) {
      this.channel.productsAdverts = this.channel.productsAdverts
        .filter(pa => pa.product !== productId)
        .concat(adverts.map(a => ({ advert: a.id, product: productId })))
    },
    updateProductAdverts (newProduct, oldProduct) {
      const advertsOldProduct = this.getAdvertsForProduct(oldProduct.id)
      const productIndex = this.channel.products.indexOf(oldProduct)
      this.removeProduct(oldProduct)
      this.channel.products.splice(productIndex, 0, newProduct[0])
      this.updateAdvertsForProduct(newProduct[0].id, advertsOldProduct)
    }
  }
}
